import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, from } from 'rxjs';
import { map, mergeMap, shareReplay, take, toArray } from 'rxjs/operators';
import { InstructionContentService } from './instruction-content.service';
import {
  INSTRUCTION_PAGES,
  instructionTitleKey
} from '../modules/help/instruction-catalog';
import {
  SearchHit,
  SearchablePage,
  searchPages
} from '../modules/help/help-search';
import {
  extractSearchText,
  parseInstructionHtml,
  removeLeadingTitle
} from '../modules/help/instruction-html';

/** Parallel page requests while the index is built. */
const CONCURRENT_REQUESTS = 6;

@Injectable({
  providedIn: 'root'
})
export class InstructionSearchService {
  private contentService = inject(InstructionContentService);
  private translateService = inject(TranslateService);

  /** Search index by language, built on the first search in that language. */
  private indexes = new Map<string, Observable<SearchablePage[]>>();

  search(query: string, lang: string): Observable<SearchHit[]> {
    return this.index(lang).pipe(map((pages) => searchPages(pages, query)));
  }

  /** Starts building the index ahead of the first search. */
  prepare(lang: string): void {
    this.index(lang).pipe(take(1)).subscribe();
  }

  private index(lang: string): Observable<SearchablePage[]> {
    let index = this.indexes.get(lang);
    if (!index) {
      index = from(INSTRUCTION_PAGES).pipe(
        mergeMap(
          (page, order) =>
            this.contentService.getContent(page.slug, lang).pipe(
              take(1),
              map((html) => ({
                order,
                page: this.toSearchable(page.slug, html)
              }))
            ),
          CONCURRENT_REQUESTS
        ),
        toArray(),
        map((pages) =>
          pages.sort((a, b) => a.order - b.order).map((p) => p.page)
        ),
        shareReplay(1)
      );
      this.indexes.set(lang, index);
    }
    return index;
  }

  private toSearchable(slug: string, html: string | null): SearchablePage {
    const title = this.translateService.instant(instructionTitleKey(slug));
    if (!html) {
      return { slug, title, headings: [], blocks: [] };
    }
    const fragment = parseInstructionHtml(html);
    removeLeadingTitle(fragment, title);
    return { slug, title, ...extractSearchText(fragment) };
  }
}
